import _ from 'lodash';

import {ActivityModel, ActivityVisualTypesEnum} from '../models/api/activityModel';
import {AttachmentModel} from '../models/api/attachmentModel';
import {CardModel} from '../models/api/cardModel';
import {CardNoteModel} from '../models/api/cardNoteModel';
import {ChannelModel} from '../models/api/channelModel';
import {buildContactFromContactUid} from '../models/api/contactModel';
import {ConversationModel} from '../models/api/conversationModel';
import {ConversationTrackerStagesEnum} from '../models/api/conversationTrackerModel';
import {InboxModel} from '../models/api/inboxModel';
import {MessageModel, MessageVisualStatusesEnum} from '../models/api/messageModel';
import {RecipientRolesEnum} from '../models/api/recipientModel';
import {TagModel} from '../models/api/tagModel';
import {TeammateModel} from '../models/api/teammateModel';
import {TopicModel} from '../models/api/topicModel';
import {VisualRecipientModel} from '../models/api/visualRecipientModel';
import {buildPublicId} from '../models/applications/publicIdTypes';
import {AppState} from '../store/appState';
import {findMessageRecipients, makeGetVisualRecipient} from '../store/data/contacts/recipientsSelectors';
import {makeGetConversationInboxes} from '../store/data/conversations/conversationsSelectors';
import {getTags} from '../store/data/tags/tagsSelectors';
import {FindTeammate, getFindTeammate} from '../store/data/teammates/lazyTeammatesSelectors';

/*
 * Types.
 */

export interface PluginEventConversation {
  conversation: ConversationModel;
  recipient: VisualRecipientModel;
  assignee?: TeammateModel;
  tags: ReadonlyArray<TagModel>;
  inboxes: ReadonlyArray<InboxModel>;
  followers: ReadonlyArray<TeammateModel>;
}

export interface PluginEvent extends PluginEventConversation {
  messages: ReadonlyArray<MessageModel>;
}

/*
 * Transform conversation to event.
 */

const trackerFollowerStages = [
  ConversationTrackerStagesEnum.SUBSCRIBER,
  ConversationTrackerStagesEnum.ASSIGNEE,
];

export function conversationToPluginEventConversation(
  state: AppState,
  conversation: ConversationModel,
  teammateId: number,
): PluginEventConversation | undefined {
  // Find the associated resources.
  const conversationSelector = () => conversation;
  const recipient = makeGetVisualRecipient(
    conversationSelector,
    () => teammateId,
    'pluginHelpers',
  )(state, {});
  if (!recipient) {
    return undefined;
  }

  const findTeammate = getFindTeammate(state);
  const {assigneeId} = conversation;
  const assignee =
    (assigneeId && findTeammate(assigneeId, 'conversationToPluginEventConversation assignee')) || undefined;

  const allTags = getTags(state);
  const tags = _(conversation.tagIds)
    .map((id) => allTags[id])
    .compact()
    .value();

  const getConversationInboxes = makeGetConversationInboxes(() => conversation);
  const inboxes = getConversationInboxes(state, {});

  const followers = _(conversation.trackers)
    .values()
    .filter((t) => Boolean(t && trackerFollowerStages.includes(t.stage)))
    .map((t) => t && findTeammate(t.teammateId, 'conversationToPluginEventConversation trackers'))
    .compact()
    .value();

  // Build a plugin conversation using this data.
  return {
    conversation,
    recipient,
    assignee,
    tags,
    inboxes,
    followers,
  };
}

/*
 * Serialization.
 */

export function eventToPluginJSON(state: AppState, props: PluginEvent) {
  const {recipient, conversation, messages} = props;
  const message = messages[messages.length - 1];
  const otherMessages = messages.slice(0, -1);

  return {
    type: 'conversation',
    conversation: pluginEventConversationToPluginJSON(props),
    contact: recipientToPluginJSON(recipient),
    message: message && messageToPluginJSON(state, conversation, message),
    otherMessages: otherMessages.map((m) => messageToPluginJSON(state, conversation, m)),
  };
}

export function channelToPluginJSON(channel: ChannelModel, isPrivate: boolean) {
  return {
    id: buildPublicId('channel', channel.id),
    type: channel.type,
    type_name: channel.typeName,
    is_private: isPrivate,
    message_type: channel.messageType,
    address: channel.address,
    send_as: channel.sendAddress,
  };
}

export function inboxToPluginJSON(inbox: InboxModel) {
  return {
    id: buildPublicId('inbox', inbox.id),
    namespace: inbox.namespace,
    alias: inbox.alias,
    name: inbox.name,
    teammates_ids: inbox.teammateIds,
    is_public: inbox.isPublic,
    color: inbox.color,
    updated_at: inbox.updatedAt,
  };
}

export function pluginEventConversationToPluginJSON(props: PluginEventConversation) {
  const {conversation, recipient, tags, inboxes, followers, assignee} = props;
  return {
    id: buildPublicId('conversation', conversation.id),
    namespace: conversation.namespace,
    message_type: conversation.messageType,
    contact: recipientToPluginJSON(recipient),
    status: conversation.teamStatus,
    link: conversation.link,
    updated_at: conversation.updatedAt,
    num_messages: conversation.numMessages,
    tags: tags.map((tag) => tag.alias),
    inboxes: inboxes.map((inbox) => inbox.alias),
    followers: followers.map((follower) => follower.alias),
    assignee: assignee && assignee.alias,
    subject: conversation.subject,
    summary: conversation.summary,
    // Expose resources ID to help developers fill the gap between plugin data & API data.
    assignee_id: assignee && buildPublicId('teammate', assignee.id),
    tag_ids: tags.map((tag) => buildPublicId('tag', tag.id)),
    inbox_ids: inboxes.map((inbox) => buildPublicId('inbox', inbox.id)),
    follower_ids: followers.map((follower) => buildPublicId('teammate', follower.id)),
    has_draft: Boolean(conversation.lastDraft),
  };
}

export function cardToPluginJson(card: CardModel) {
  return {
    id: buildPublicId('card', card.id),
    url: card.url,
    name: card.name,
    namespace: card.namespace,
    description: card.description,
    links: card.links.map((l) => l.url),
    edited: card.wasNameEdited,
    bio: card.bio,
    avatar: card.avatarUrl,
    account_id: card.accountId,
    num_notes: card.numberOfNotes,
    contacts: card.contactUids.map(buildContactFromContactUid).map((contact) => ({
      source: contact.source,
      handle: contact.handle,
    })),
    custom_field_attributes: card.customFieldAttributes?.map((attribute) => ({
      custom_field_id: attribute.id,
      value: attribute.value,
    })),
    num_notes_via_namespace: card.numberOfNotesViaNamespaces,
    group_ids: card.groupIds.map((groupId) => buildPublicId('cardGroup', groupId)),
    type: card.type,
    updated_at: card.updatedAt,
  };
}

export function recipientToPluginJSON(recipient: VisualRecipientModel) {
  const {role, name, contact, fallbackContact, card, channel} = recipient;
  // We always have a recipient and a contact.
  const result = {
    handle: channel ? channel.sendAddress : contact.handle,
    source: contact.source,
    url: contact.url,
    email: fallbackContact && fallbackContact.handle,
    role,
    display_name: channel ? channel.displayName : name,
  };

  // If we don't have a card return what we have.
  if (!card) {
    return result;
  }

  // Otherwise, add properties.
  return {
    ...result,
    id: buildPublicId('card', card.id),
    card_url: card.url,
    display_name: card.name,
    description: card.bio,
    avatar: card.avatarUrl,
    num_notes: card.numberOfNotes,
    card: cardToPluginJson(card),
  };
}

export function messageToPluginJSON(state: AppState, conversation: ConversationModel, message: MessageModel) {
  const recipients = findMessageRecipients(state, conversation.namespace, message);
  const from = recipients.find((r) => r.role === RecipientRolesEnum.FROM);

  const partialMessageJSON = {
    id: buildPublicId('message', message.id),
    ext_id: message.extId,
    type: message.type,
    status: message.status,
    date: message.sentAt,
    inbound: message.isInbound,
    blurb: message.blurb,
    recipient_opened_date: undefined,
    from: from ? recipientToPluginJSON(from) : undefined,
    recipients: recipients.map((r) => recipientToPluginJSON(r)),
    attachments: message.attachments.map(attachmentToPluginJSON),
  };

  if (message.visualStatus === MessageVisualStatusesEnum.PARTIAL) {
    return partialMessageJSON;
  }

  return {
    ...partialMessageJSON,
    subject: message.subject,
    body: message.body,
    text: message.text,
  };
}

export function activityToPluginJSON(activity: ActivityModel): any {
  const base = {
    id: buildPublicId('activity', activity.id),
    type: activity.type,
  };

  switch (activity.visualType) {
    case ActivityVisualTypesEnum.MESSAGE:
      return {...base, message: {uid: activity.messageUid}};

    case ActivityVisualTypesEnum.COMMENT:
      return {...base, comment: {uid: activity.commentUid}};

    default:
      return base;
  }
}

export function attachmentToPluginJSON(attachment: AttachmentModel) {
  return {
    uid: attachment.uid,
    filename: attachment.name,
    content_type: attachment.contentType,
    size: attachment.size,
    metadata: {is_inline: attachment.isInline},
  };
}

export function topicToPluginJSON(topic: TopicModel) {
  return {
    id: buildPublicId('topic', topic.id),
    alias: topic.alias,
    url: topic.url,
    type: topic.type,
    name: topic.name,
    app_link: topic.appLink,
    ext_link: topic.externalLink,
    link: topic.appLink,
  };
}

export function teammateToPluginJSON(teammate: TeammateModel, isAdmin?: boolean) {
  return {
    id: buildPublicId('teammate', teammate.id),
    url: teammate.url,
    alias: teammate.alias,
    given_name: teammate.givenName,
    family_name: teammate.familyName,
    email: teammate.email,
    avatar: teammate.avatarUrl,
    admin: isAdmin,
  };
}

export function cardNoteToPluginJSON(findTeammate: FindTeammate, cardNote: CardNoteModel) {
  const author = findTeammate(cardNote.authorId, 'cardNoteToPluginJSON');
  return {
    body: cardNote.body,
    text: cardNote.text,
    date: cardNote.createdAt,
    author: (author && author.alias) || undefined,
  };
}

export function tagToPluginJSON(tag: TagModel) {
  return {
    id: buildPublicId('tag', tag.id),
    alias: tag.alias,
    name: tag.name,
  };
}
